import { tenantUrl, toUrlRid } from '../../utils/tenantPath';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '../../routing';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import Icon from '../../components/Icon';
import { ROLE_LABELS } from './modals';

// Global search — one component at two sizes (hero on the overview, compact in
// the topbar everywhere else), one result renderer, one ranking.
//
// A PERSISTENT FIELD, not a ⌘K modal. A palette pays for itself at dozens of
// invocations a day across a large surface; this is a handful of people hitting
// ~8 destinations, at least one of them reactively while on the phone with a
// customer. And the real gesture here is PASTE, not type — ops arrive with an
// email or a doc number already in the clipboard — which wants a field that is
// already focused, not a modal that must first be summoned.
//
// Search is NAVIGATION ONLY. It never fires a mutation: this console holds a
// delete-user with no reference check, and "type three letters, hit Enter, a
// write happens" is the wrong ergonomics for that. Every row goes to a card
// where the action lives behind its own labelled control.

// Group order is FIXED, never relevance-shuffled. A stable layout is worth more
// than optimal ordering when the same few people use it every day and learn
// where to look.
export const GROUP_ORDER = ['users', 'restaurants', 'networks', 'suppliers', 'documents'];

export const GROUP_LABELS = {
  users:       'משתמשים',
  restaurants: 'מסעדות',
  networks:    'רשתות',
  suppliers:   'ספקים',
  documents:   'מסמכים',
};

const GROUP_ICONS = {
  users:       'users',
  restaurants: 'store',
  networks:    'network',
  suppliers:   'suppliers',
  documents:   'documents',
};

const DOC_STATUS_ICON = { 'תקין': 'check', 'טיוטה': 'alert', 'לא תקין': 'alert' };

/**
 * Flattens the grouped payload into the single list the arrow keys walk.
 * Group headers are VISUAL, not stops — ↓ from the last user lands on the first
 * restaurant. Exported for tests: this ordering is the contract.
 */
export function flattenResults(data) {
  if (!data) return [];
  const out = [];
  for (const key of GROUP_ORDER) {
    const group = data[key];
    if (!group?.items?.length) continue;
    group.items.forEach((item, i) => {
      out.push({ group: key, item, first: i === 0, total: group.total, shown: group.items.length });
    });
  }
  return out;
}

/** Where selecting a result takes you. Exported so the tests can pin it. */
export function destinationFor(group, item) {
  switch (group) {
    case 'users':       return `/command-center/people/${item.id}`;
    case 'restaurants': return `/command-center/customers/${item.id}`;
    case 'networks':    return `/command-center/customers?tab=networks&highlight=${item.id}`;
    // Deliberately the Command Center's customer card with the supplier
    // highlighted — NOT the customer app's /supplier/:id, which would need a
    // tenant switch just to answer "does this supplier exist here?".
    case 'suppliers':   return `/command-center/customers/${item.restaurant_id}#supplier-${item.id}`;
    // The one place a tenant handoff is unavoidable. The restaurant rides in the
    // URL and the CUSTOMER app performs the switch — the Command Center never
    // writes restaurantId itself (that would hijack the ops person's other tab).
    case 'documents':   return toUrlRid(item.restaurant_id) == null ? null : tenantUrl(item.restaurant_id, `/document/${item.id}`);
    default:            return '/command-center';
  }
}

/** Documents leave the console; everything else stays inside it. */
export const leavesConsole = (group) => group === 'documents';

function rowContent(group, item) {
  switch (group) {
    case 'users':
      return {
        ident: item.email,
        qual: ROLE_LABELS[item.role] || item.role,
        tenant: item.role === 'admin'
          ? 'כל המסעדות'
          : item.restaurant_count === 1
            ? item.first_restaurant_name
            : `${item.restaurant_count} מסעדות`,
      };
    case 'restaurants':
      return {
        ident: item.name,
        qual: item.business_id ? `ח.פ. ${item.business_id}` : 'חסר ח.פ.',
        qualBad: !item.business_id,
        tenant: item.network_name || '',
      };
    case 'networks':
      return { ident: item.name, qual: '', tenant: `${item.restaurant_count} מסעדות` };
    case 'suppliers':
      return { ident: item.name, qual: `${item.doc_count} מסמכים`, tenant: item.restaurant_name || '' };
    case 'documents':
      return {
        ident: `${item.doc_type} ${item.doc_num || ''}`.trim(),
        qual: [item.date, formatCurrency(item.total), item.status].filter(Boolean).join(' · '),
        tenant: item.restaurant_name || '',
        statusIcon: DOC_STATUS_ICON[item.status],
      };
    default:
      return { ident: '', qual: '', tenant: '' };
  }
}

export default function CommandCenterSearch({ hero = false, autoFocus = false, initialQuery = '' }) {
  const [q, setQ] = useState(initialQuery);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const rows = useMemo(() => flattenResults(data), [data]);

  // ⌘/Ctrl+K focuses this field from anywhere in the console.
  //
  // Bound on e.code, NEVER e.key: under a Hebrew keyboard layout e.key is 'ק',
  // so the obvious `e.key === 'k'` implementation silently does nothing for the
  // people this tool is for. preventDefault because Ctrl+K is "search from the
  // address bar" in Chrome on Windows.
  //
  // There is deliberately no '/' shortcut: on the Hebrew layout the physical
  // slash key types '.', so it would be bound to a key users cannot find.
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'KeyK' || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const term = q.trim();
    const minLen = /^\d+$/.test(term) ? 1 : 2;
    if (term.length < minLen) {
      setData(null);
      setFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      commandCenterApi.search(term)
        .then(r => {
          if (cancelled) return;
          setData(r.data);
          setFailed(false);
          setActive(0);
          setOpen(true);
        })
        .catch(() => { if (!cancelled) { setFailed(true); setData(null); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const go = useCallback((row, newTab = false) => {
    if (!row) return;
    const to = destinationFor(row.group, row.item);
    if (!to) return;
    setOpen(false);
    if (newTab || leavesConsole(row.group)) window.open(to, '_blank', 'noopener');
    else navigate(to);
  }, [navigate]);

  const onKeyDown = (e) => {
    // Arrow keys are VERTICAL ONLY — ←/→ direction semantics invert under RTL
    // and nobody agrees on which way they should go.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!rows.length) return;
      e.preventDefault();
      setOpen(true);
      setActive(cur => {
        const next = e.key === 'ArrowDown' ? cur + 1 : cur - 1;
        return (next + rows.length) % rows.length; // wraps
      });
      return;
    }
    if (e.key === 'Enter') {
      // Nothing highlighted but exactly one result overall — take it.
      const row = rows[active] || (rows.length === 1 ? rows[0] : null);
      if (row) { e.preventDefault(); go(row, e.metaKey || e.ctrlKey); }
      return;
    }
    if (e.key === 'Escape') {
      // First press closes the results but KEEPS the query so it can be edited;
      // second press clears. Never navigates away.
      e.preventDefault();
      if (open) setOpen(false);
      else setQ('');
    }
  };

  // Mirrors the server's gate (lib/ccSearch.js): 2 chars, or 1 for a digits-only
  // query, since a ח.פ. or doc-number prefix is meaningful immediately.
  const term = q.trim();
  const tooShort = term.length > 0 && term.length < (/^\d+$/.test(term) ? 1 : 2);
  const showPanel = open && (failed || loading || rows.length > 0 || term.length >= 1);

  return (
    <div className={`cc-search${hero ? ' cc-search--hero' : ''}`}>
      <div className="cc-search-field">
        <Icon name="search" size={16} className="cc-search-ic" />
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="cc-search-listbox"
          aria-autocomplete="list"
          aria-activedescendant={rows[active] ? `cc-res-${active}` : undefined}
          placeholder="חפש משתמש, מסעדה, ח.פ., ספק או מספר מסמך…"
          aria-label="חיפוש"
        />
        {/* The shortcut hint lives INSIDE the field on the hero. As its own line
            beneath it, it cost a full row of the fold to teach one accelerator.
            dir="ltr" or bidi reorders it into "K⌘" on this RTL page. */}
        {hero && !q && <kbd className="cc-search-kbd" dir="ltr">⌘K</kbd>}
        {q && (
          <button type="button" className="cc-search-clear" onClick={() => { setQ(''); inputRef.current?.focus(); }} aria-label="נקה חיפוש">
            <Icon name="close" size={15} />
          </button>
        )}
      </div>

      {showPanel && (
        <div className="cc-search-panel" id="cc-search-listbox" role="listbox" aria-label="תוצאות חיפוש">
          {failed ? (
            // A failed search must never look like "no results" — that would
            // read as "this customer doesn't exist" and send ops down a wrong path.
            <div className="cc-search-empty" style={{ color: '#b91c1c' }}>
              החיפוש נכשל — נסה שוב
            </div>
          ) : rows.length === 0 ? (
            <div className="cc-search-empty">
              {/* Below the minimum length nothing has been searched yet, so
                  "אין תוצאות" would be a claim about data we never queried —
                  and it reads as "this customer doesn't exist". */}
              {tooShort ? 'המשך להקליד…' : loading ? 'מחפש…' : 'אין תוצאות'}
            </div>
          ) : (
            GROUP_ORDER.map(groupKey => {
              const groupRows = rows.filter(r => r.group === groupKey);
              if (!groupRows.length) return null;
              const { total, shown } = groupRows[0];
              return (
                <div key={groupKey}>
                  <div className="cc-search-group">{GROUP_LABELS[groupKey]} · {total}</div>
                  {groupRows.map(row => {
                    const idx = rows.indexOf(row);
                    const c = rowContent(row.group, row.item);
                    return (
                      <a
                        key={`${row.group}-${row.item.id}`}
                        id={`cc-res-${idx}`}
                        role="option"
                        aria-selected={idx === active}
                        href={destinationFor(row.group, row.item) || undefined}
                        aria-disabled={!destinationFor(row.group, row.item)}
                        target={leavesConsole(row.group) ? '_blank' : undefined}
                        rel={leavesConsole(row.group) ? 'noreferrer' : undefined}
                        className={`cc-search-row${idx === active ? ' is-active' : ''}`}
                        onMouseEnter={() => setActive(idx)}
                        onClick={(e) => {
                          // Let ⌘-click / middle-click do their native thing.
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                          e.preventDefault();
                          go(row);
                        }}
                      >
                        <Icon name={GROUP_ICONS[row.group]} size={15} className="cc-muted" />
                        <span className="cc-row-ident">{c.ident}</span>
                        {c.statusIcon && <Icon name={c.statusIcon} size={13} className="cc-muted" />}
                        {c.qual && (
                          <span className="cc-row-qual" style={c.qualBad ? { color: '#b91c1c' } : undefined}>
                            {c.qual}
                          </span>
                        )}
                        {c.tenant && <span className="cc-row-tenant">{c.tenant}</span>}
                        {leavesConsole(row.group) && <Icon name="external" size={12} className="cc-muted" />}
                      </a>
                    );
                  })}
                  {total > shown && (
                    <div className="cc-search-more">ועוד {total - shown}</div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
